import { getAutogeneratedBanner } from '../banners';
import {
  assertNoFieldNameCollisions,
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  getEnumMemberKind,
  getFieldDocLines,
  getSensitiveKeys,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

function getRustCoercedTypeString(coerced: CoercedType): string {
  if (coerced === 'int') return 'i64';
  if (coerced === 'number') return 'f64';
  if (coerced === 'boolean') return 'bool';
  if (coerced === 'object') return 'serde_json::Value';
  // Rust has no literal-union types, so an enum field gets its members' scalar type — the blob
  // carries the *coerced* value, so a numeric enum must be numeric or serde fails at runtime
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    const kind = getEnumMemberKind(coerced.enum);
    if (kind === 'int') return 'i64';
    if (kind === 'number') return 'f64';
    if (kind === 'boolean') return 'bool';
    if (kind === 'mixed') return 'serde_json::Value';
  }
  // the blob value is a real JSON array, so Vec<T> deserializes natively; records stay
  // serde_json::Value (a typed map would drag in a HashMap import for marginal benefit)
  if (typeof coerced === 'object' && 'arrayOf' in coerced) {
    return `Vec<${getRustCoercedTypeString(coerced.arrayOf)}>`;
  }
  if (typeof coerced === 'object' && 'recordOf' in coerced) {
    return 'serde_json::Value';
  }
  return 'String';
}

// Rust keywords that would collide with a lowercased key used as a field name.
const RUST_KEYWORDS = new Set([
  'as',
  'break',
  'const',
  'continue',
  'crate',
  'dyn',
  'else',
  'enum',
  'extern',
  'false',
  'fn',
  'for',
  'if',
  'impl',
  'in',
  'let',
  'loop',
  'match',
  'mod',
  'move',
  'mut',
  'pub',
  'ref',
  'return',
  'self',
  'static',
  'struct',
  'super',
  'trait',
  'true',
  'type',
  'unsafe',
  'use',
  'where',
  'while',
  'async',
  'await',
  'union',
  // reserved-but-unused keywords — also illegal as bare identifiers (Rust 2018+)
  'abstract',
  'become',
  'box',
  'do',
  'final',
  'gen',
  'macro',
  'override',
  'priv',
  'try',
  'typeof',
  'unsized',
  'virtual',
  'yield',
]);
// keywords that cannot be raw identifiers (`r#...`) — suffix with `_` instead
// (`Self` doesn't need an entry: keys are lowercased before this lookup)
const RUST_NON_RAW_KEYWORDS = new Set(['crate', 'self', 'super']);

// keys are already identifier-safe (validated upstream), so lowercasing yields a legal identifier;
// escape keyword collisions with a raw identifier so e.g. `TYPE` -> `r#type` still compiles
function envKeyToRustFieldName(key: string): string {
  const name = key.toLowerCase();
  if (RUST_KEYWORDS.has(name)) return RUST_NON_RAW_KEYWORDS.has(name) ? `${name}_` : `r#${name}`;
  return name;
}

function fieldDocComment(field: ResolvedFieldType): Array<string> {
  return getFieldDocLines(field).map((line) => `    /// ${line}`);
}

export function generateRustEnvSrc(fields: Array<ResolvedFieldType>): string {
  // struct/loader cover representable keys; skipped keys stay as raw env vars (std::env::var).
  // a bare `_` is the wildcard pattern, not an identifier — it can't be a field name
  const { safe, skipped } = partitionRepresentableKeys(fields, (k) => k === '_');
  assertNoFieldNameCollisions(safe, envKeyToRustFieldName, 'Rust');
  // SENSITIVE_KEYS covers every sensitive key (incl. skipped ones) so redaction doesn't miss them
  const sensitiveKeys = getSensitiveKeys(fields);

  const structFields = safe.flatMap((field) => {
    const baseType = getRustCoercedTypeString(field.coerced);
    const rustType = field.isRequired ? baseType : `Option<${baseType}>`;
    return [
      ...fieldDocComment(field),
      // explicit per-field rename: the wire name is the exact env key, decoupled from the Rust
      // identifier (a struct-level rename_all would mis-map mixed-case or already-snake keys)
      `    #[serde(rename = ${JSON.stringify(field.key)})]`,
      `    pub ${envKeyToRustFieldName(field.key)}: ${rustType},`,
    ];
  });

  // custom Debug so `{:?}` never prints secrets (a derived Debug would) — sensitive fields render
  // as `<redacted>`; non-sensitive fields print normally. The emitter knows which is which.
  const debugFields = safe.map((field) => {
    const name = envKeyToRustFieldName(field.key);
    const value = field.isSensitive ? '&"<redacted>"' : `&self.${name}`;
    return `            .field(${JSON.stringify(field.key)}, ${value})`;
  });

  const src = [
    getAutogeneratedBanner('//'),
    '',
    '//! Generated by varlock; `load()` parses the __VARLOCK_ENV blob into a typed, coerced `Env`.',
    '',
    'use serde::Deserialize;',
    '',
    ...skippedKeysComment(skipped, '//'),
    '/// Coerced env schema (matches varlock coercion).',
    '#[derive(Clone, Deserialize)]',
    'pub struct Env {',
    ...structFields,
    '}',
    '',
    'impl std::fmt::Debug for Env {',
    '    fn fmt(&self, f: &mut std::fmt::Formatter<\'_>) -> std::fmt::Result {',
    '        f.debug_struct("Env")',
    ...debugFields,
    '            .finish()',
    '    }',
    '}',
    '',
    '/// Keys whose values are sensitive — use to build your own redaction / leak-scanning.',
    `pub const SENSITIVE_KEYS: &[&str] = &[${sensitiveKeys.map((k) => JSON.stringify(k)).join(', ')}];`,
    '',
    '/// Parse the `__VARLOCK_ENV` blob (injected by `varlock run`) into a typed, coerced `Env`.',
    'pub fn load() -> Result<Env, Box<dyn std::error::Error>> {',
    '    let blob = std::env::var("__VARLOCK_ENV")',
    `        .map_err(|_| ${JSON.stringify(BLOB_MISSING_MSG)})?;`,
    `    if blob.starts_with(${JSON.stringify(ENCRYPTED_BLOB_PREFIX)}) {`,
    `        return Err(${JSON.stringify(BLOB_ENCRYPTED_MSG)}.into());`,
    '    }',
    '    let graph: serde_json::Value = serde_json::from_str(&blob)',
    '        .map_err(|e| format!("varlock: failed to parse __VARLOCK_ENV: {e}"))?;',
    '    let mut flat = serde_json::Map::new();',
    '    if let Some(config) = graph.get("config").and_then(|c| c.as_object()) {',
    '        for (key, entry) in config {',
    '            if let Some(value) = entry.get("value") {',
    '                flat.insert(key.clone(), value.clone());',
    '            }',
    '        }',
    '    }',
    '    Ok(serde_json::from_value(serde_json::Value::Object(flat))?)',
    '}',
    '',
  ];

  return src.join('\n');
}
